/**
 * Pressure fields are saved by the app as { bar: '<converted>', current: { value, unity } }.
 * `current` holds exactly what the technician typed/selected (e.g. "0.5 mbar"); `.bar` is a
 * lossy bar-equivalent conversion (rounded to 3 decimals), only useful as a display fallback
 * for older records saved before the unit selector existed (no `current`).
 */
export function formatPressureValue(pressure) {
  if (!pressure) {
    return '0 bar';
  }

  if (pressure.current && pressure.current.value !== undefined && pressure.current.value !== '') {
    return `${pressure.current.value} ${pressure.current.unity || 'bar'}`;
  }

  return `${pressure.bar || '0'} bar`;
}

const BAR_CONVERSION_FACTORS = {
  PSI: 0.0689476,
  kgf: 0.9807,
  Pa: 1 / 100000,
  MPa: 10,
  inHg: 0.0338639,
  mmHg: 0.00133322,
  mbar: 0.001,
  bar: 1,
};

/**
 * Precise bar-equivalent of a pressure value, converted from `current.value`/`current.unity`
 * when available (avoids the lossy `.toFixed(3)` rounding baked into the app's stored `.bar`,
 * which can zero out small values entered in mbar/Pa). Falls back to the stored `.bar` for
 * legacy records without `current`. Returns `null` when there's no usable value at all, so
 * callers can render a placeholder instead of "0".
 */
export function toBarNumber(pressure) {
  if (!pressure) {
    return null;
  }

  if (pressure.current && pressure.current.value !== undefined && pressure.current.value !== '') {
    const numericValue = parseFloat(pressure.current.value);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    const factor = BAR_CONVERSION_FACTORS[pressure.current.unity];
    return numericValue * (factor === undefined ? 1 : factor);
  }

  if (pressure.bar !== undefined && pressure.bar !== '') {
    const numericValue = parseFloat(pressure.bar);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  return null;
}

export function formatPressureRange(start, end) {
  const unity = (start && start.current && start.current.unity)
    || (end && end.current && end.current.unity)
    || 'bar';

  const startValue = (start && start.current && start.current.value !== '' && start.current.value !== undefined)
    ? start.current.value
    : (start && start.bar) || '0';

  const endValue = (end && end.current && end.current.value !== '' && end.current.value !== undefined)
    ? end.current.value
    : (end && end.bar) || '0';

  return `${startValue} - ${endValue} ${unity}`;
}
